import { useMutation } from '@apollo/client';
import { useState } from 'react';

import { useAscContext } from '@neo.tax/asc/context';
import {
  ExcelParseError,
  UPLOAD_ASC_WAGE_REPORT,
  UploadAscWageReportMutation,
  UploadAscWageReportMutationVariables,
  evictCache,
} from '@neo.tax/shared';

import { UploadAscWageReportStatus } from './upload-asc-wage-report-status.enums';

interface UploadAscWageReport {
  status: UploadAscWageReportStatus;
  errors?: ExcelParseError[] | null;
  upload: () => Promise<void>;
  reset: () => void;
}

export const useUploadAscWageReport = (file: File | undefined): UploadAscWageReport => {
  const { fiscalYearId, periodName } = useAscContext();
  const [status, setStatus] = useState<UploadAscWageReportStatus>(UploadAscWageReportStatus.NOT_STARTED);
  const [uploadAscWageReport, { data }] = useMutation<
    UploadAscWageReportMutation,
    UploadAscWageReportMutationVariables
  >(UPLOAD_ASC_WAGE_REPORT);

  const reset = (): void => {
    setStatus(UploadAscWageReportStatus.NOT_STARTED);
  };

  const upload = async (): Promise<void> => {
    setStatus(UploadAscWageReportStatus.UPLOADING);
    await uploadAscWageReport({
      variables: {
        fiscalYearId,
        periodName,
        file,
      },
      update: (cache) => {
        evictCache(cache, ['getAscWages', 'getAscWageSummary']);
      },
      onError: () => {
        setStatus(UploadAscWageReportStatus.ERROR);
      },
      onCompleted: (data) => {
        if (data.uploadAscWageReport.errors) {
          setStatus(UploadAscWageReportStatus.ERROR);
        } else {
          setStatus(UploadAscWageReportStatus.SUCCESS);
        }
      },
    });
  };

  return {
    status,
    errors: data?.uploadAscWageReport.errors,
    upload,
    reset,
  };
};
